import { useIsRbacAdminWithLoading } from 'hooks/useIsRbacAdmin'

// The activation gate: the account admin, and only the account admin.
//
// This is exact server parity rather than an approximation of it. The audited PUT
// authorizes via permitted_to?, which short-circuits on merchant_user_merchant&.admin?
// — and under RBAC that IS the preset-admin-role check useIsRbacAdmin performs. So the
// gate cannot be wider or narrower than the endpoint it unlocks.
//
// No billing term and no manage:RecoveryAI floor: the preset admin holds every
// permission by construction (rbac_seeder seeds each resource at can_manage: true), so
// both were redundant once legacy stopped mattering.
//
// useIsRbacAdminWithLoading, NOT useIsAdminWithLoading: the latter ORs in
// legalDocsEnforced, which would make canActivate true for ANY user reaching this route
// during a legal-docs block — a widening on a billing-committing PUT.
// A discriminated union rather than { canActivate, isLoading }: the latter admits
// { canActivate: true, isLoading: true }, which this hook never produces, and callers
// then have to rediscover that invariant — spelling it four different ways, one of
// them dead (`canActivate && !gateLoading`). Three named states make the branches
// exhaustive and the impossible one unrepresentable.
export type ActivationGate =
  | { state: 'resolving' }
  | { state: 'admin' }
  | { state: 'not-admin' }

export const useIsActivationAdmin = (): ActivationGate => {
  const { isRbacAdmin, isLoading } = useIsRbacAdminWithLoading()
  if (isLoading) return { state: 'resolving' }
  return { state: isRbacAdmin ? 'admin' : 'not-admin' }
}

export default useIsActivationAdmin
